import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useApp } from '../contexts/AppContext';
import { authService } from '../services/auth';
import { resumeAllSessions } from '../services/api';
import api from '../api'; 
import { jwtDecode } from 'jwt-decode'; 

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const bundle = JSON.parse(localStorage.getItem('auth') || 'null');
const s = localStorage.getItem("user");
console.log("s:", !!s);

const t = localStorage.getItem(config.tokenKey);
console.log("t:", !!t);

const isUserStored = bundle ? !!bundle.user : !!s;
const isTokenStored = bundle ? !!bundle.token : !!t;

// Track verification state globally to prevent duplicate calls
let globalVerifyInProgress = false;
let lastVerifyTime = 0;
const VERIFY_THROTTLE = 2000; // 2 seconds minimum between verify calls

export const AuthProvider = ({ children }) => {
  // Track whether we've already shown auth failure toast to avoid duplicates
  const authErrorToastShown = useRef(false);
  
  // Set flag to indicate AuthProvider is active
  useEffect(() => {
    window.__AUTH_PROVIDER_ACTIVE__ = true;
    return () => {
      window.__AUTH_PROVIDER_ACTIVE__ = false;
    };
  }, []);

  // Check for Composio OAuth permission denial flag right away
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError === 'permissions_required') {
      toast.error('Please approve all requested Gmail permissions to continue.');
    }
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(
    (isUserStored && isTokenStored) ? true : false
  );
  // make initial google credentials available
  if (bundle?.google_credentials && !sessionStorage.getItem('google_credentials')) {
    sessionStorage.setItem('google_credentials', bundle.google_credentials);
    localStorage.setItem('google_credentials', bundle.google_credentials);
  }

  // Fallback: hydrate credentials from cookie if still missing (legacy sessions)
  if (!sessionStorage.getItem('google_credentials')) {
    const cookieStr = document.cookie.split('; ').find(c => c.startsWith('google_credentials='));
    if (cookieStr) {
      const g = decodeURIComponent(cookieStr.split('=')[1] || '');
      if (g) {
        sessionStorage.setItem('google_credentials', g);
        localStorage.setItem('google_credentials', g);
        console.log('[Cred-debug] hydrated google_credentials from cookie');
      }
    }
  }

  console.log("isAuthenticated1:", isAuthenticated);
 
  const [user, setUser] = useState(s);
  const [token, setToken] = useState(t);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Don't initialize from sessionStorage - only set when verify endpoint confirms resume
  const [resumeState, setResumeState] = useState(null);
  const [calendlyLink, setCalendlyLink] = useState(null); // Track calendly link from auth verify
  const navigate = useNavigate();
  const { addNotification } = useApp();

  // useEffect(() => {
  //   // const storedToken = localStorage.getItem(config.tokenKey);
  //   // const storedUser = localStorage.getItem("user");

  //   if (storedToken && Object.entries(storedUser).length > 0) {
  //     try {
  //       // const parsedUser = JSON.parse(storedUser);
  //       setToken(storedToken);
  //       setUser(storedUser);
  //       setIsAuthenticated(true);
  //     } catch (err) {
  //       console.error("Invalid user data in localStorage", err);
  //       // localStorage.removeItem("user");
  //       // localStorage.removeItem(config.tokenKey);
  //     }
  //   }

  //   // setLoading(false);
  // }, []);

  // Verify token on mount and token change
  const verifyToken = useCallback(async () => {
    if (!token) {
      // No token present – user is unauthenticated. Simply update state
      setLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    // Prevent concurrent verification calls
    if (globalVerifyInProgress) {
      console.log('[AuthProvider] Verification already in progress, skipping');
      return;
    }
    
    // Throttle verification calls
    const now = Date.now();
    if (now - lastVerifyTime < VERIFY_THROTTLE) {
      console.log('[AuthProvider] Verification throttled, last verify was', now - lastVerifyTime, 'ms ago');
      setLoading(false);
      return;
    }

    globalVerifyInProgress = true;
    lastVerifyTime = now;

    try {
      // First verify token locally
      const decoded = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (!decoded || !decoded.exp || decoded.exp <= currentTime) {
        throw new Error("Token expired or invalid");
      }

            // Then verify with server (only one call will be made)
            console.log('[AuthProvider] Calling authService.verifyAuth()');
            const response = await authService.verifyAuth();
            console.log('[AuthProvider] verify_auth response:', response);
            
            if (response.valid) {
                 if (response.google_credentials) {
                    const credsStr = JSON.stringify(response.google_credentials);
                    sessionStorage.setItem('google_credentials', credsStr);
                    localStorage.setItem('google_credentials', credsStr);
                    console.log('[Cred-debug]', location.pathname, 'saved →', (credsStr||'').slice(0,60));
                    document.cookie = `google_credentials=${encodeURIComponent(credsStr)}; path=/; domain=${location.hostname}; SameSite=Lax`;
                    if (process.env.NODE_ENV === 'development') {
                      console.log('AuthProvider.verifyToken: google_credentials hydrated from verify');
                  }
                 }
                setIsAuthenticated(true);
                // Update user data from response or keep existing if not provided
                setUser(response.user || decoded);
                
                // Store calendly_link if present
                if (response.calendly_link) {
                    console.log('[AuthProvider] Setting calendly_link:', response.calendly_link);
                    setCalendlyLink(response.calendly_link);
                }
                
                // Store resume state if present
                if (response.is_resuming === true) {
                    console.log('[AuthProvider] Campaign is resuming:', response.campaign_id);
                    const newResumeState = { 
                        isResuming: true, 
                        campaignId: response.campaign_id,
                        timestamp: Date.now()
                    };
                    setResumeState(newResumeState);
                    // Don't persist to sessionStorage anymore - causes race conditions
                } else {
                    // ALWAYS clear any stale resume state if not explicitly resuming
                    console.log('[AuthProvider] Clearing resume state (is_resuming:', response.is_resuming, ')');
                    setResumeState(null);
                    localStorage.removeItem('grupa_campaigns');
                }
            } else {
                throw new Error(response.error || 'Server verification failed');
            }
        } catch (error) {
            console.error('[AuthProvider] Token verification error:', error);
            // Surface a user-friendly notification (only once)
            if (!authErrorToastShown.current) {
                toast.error('Authentication failed or expired. Please log in again and ensure all requested permissions are granted.', { id: 'auth_failed' });
                authErrorToastShown.current = true;
                addNotification({
                  type: 'error',
                  message: 'Authentication failed or expired. Please log in again and ensure all requested permissions are granted.'
                });
                setIsAuthenticated(false);
                setUser(null);
                localStorage.removeItem(config.tokenKey);
                localStorage.removeItem('google_credentials');
                sessionStorage.removeItem('google_credentials');
                setToken(null);
            
            } // end if not shown
            // Only redirect to login if not a network error
            if (!error.message.includes('Network error')) {
                // Only navigate if a token existed (i.e., user thought they were signed in)
            if (token) {
                navigate('/login');
            }
            }
        } finally {
            setLoading(false);
            globalVerifyInProgress = false;
        }
    }, [token, navigate]);

    useEffect(() => {
        verifyToken();
    }, [verifyToken]);

    // Fallback: capture google credentials passed via redirect query param
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gParam = params.get('google');
        if (gParam) {
            try {
                const credsStr = decodeURIComponent(escape(window.atob(gParam)));
                sessionStorage.setItem('google_credentials', credsStr);
                localStorage.setItem('google_credentials', credsStr);
                console.log('[Cred-debug]', location.pathname, 'saved →', (credsStr||'').slice(0,60));
                document.cookie = `google_credentials=${encodeURIComponent(credsStr)}; path=/; domain=${location.hostname}; SameSite=Lax`;
                console.log('Stored google_credentials from URL param');
                // Remove the param from URL to keep it clean
                params.delete('google');
                const newQuery = params.toString();
                const newUrl = window.location.pathname + (newQuery ? '?' + newQuery : '') + window.location.hash;
                window.history.replaceState({}, '', newUrl);
            } catch (err) {
                console.error('Failed to decode google param', err);
            }
        }
    }, []);


    // Listen for google_credentials sent via window.postMessage from the OAuth callback page
    useEffect(() => {
        function handleCredentialMessage(event) {
            // Ensure message is from an allowed origin (if provided)
            if (config.allowedOrigins && config.allowedOrigins.length && !config.allowedOrigins.includes(event.origin)) {
                return; // ignore untrusted origins
            }
            if (!event.data || event.data.type !== 'google_credentials') return;
            try {
                const credsStr = typeof event.data.data === 'string' ? event.data.data : JSON.stringify(event.data.data);
                sessionStorage.setItem('google_credentials', credsStr);
                localStorage.setItem('google_credentials', credsStr);
                console.log('[Cred-debug]', location.pathname, 'saved →', (credsStr||'').slice(0,60));
                document.cookie = `google_credentials=${encodeURIComponent(credsStr)}; path=/; domain=${location.hostname}; SameSite=Lax`;
                console.log('Received and stored google_credentials from postMessage');
            } catch (err) {
                console.error('Failed handling google_credentials message', err);
            }
        }
        window.addEventListener('message', handleCredentialMessage);
        return () => window.removeEventListener('message', handleCredentialMessage);
    }, []);

  const login = useCallback(async (authData) => {
        try {
            if (!authData.token) {
                throw new Error('No token provided');
            }

            // Validate the Google credential with our backend
            const response = await authService.authenticateWithGoogle(authData.token);

            if (!response.success || !response.token) {
                throw new Error('Backend authentication failed');
            }

            // Use the token from our backend instead of the Google token
            setIsAuthenticated(true);
            setUser(response.user);
            setToken(response.token);

            // Store the backend-validated token
            localStorage.setItem(config.tokenKey, response.token);
            localStorage.setItem('user', JSON.stringify(response.user));

            // Store Google credentials in both session and local storage for persistence across tabs
            if (response.google_credentials) {
              const credsStr = JSON.stringify(response.google_credentials);
                sessionStorage.setItem('google_credentials', credsStr);
                localStorage.setItem('google_credentials', credsStr);
                console.log('[Cred-debug]', location.pathname, 'saved →', (credsStr||'').slice(0,60));
                document.cookie = `google_credentials=${encodeURIComponent(credsStr)}; path=/; domain=${location.hostname}; SameSite=Lax`;
                if (process.env.NODE_ENV === 'development') {
                 console.log('AuthProvider: google_credentials saved to storage');
             }
                
            }
            
            addNotification({
                type: 'success',
                message: `Welcome, ${response.user.name}!`
            });

            // Auto-resume any paused campaign in background (no UI wait)
            try {
                await resumeAllSessions();
            } catch (e) {
                console.warn('Resume campaigns failed (maybe none to resume):', e.message);
            }

            navigate('/dashboard');
        } catch (error) {
            console.error('Login failed:', error);
            localStorage.removeItem(config.tokenKey);
            localStorage.removeItem('user');
            setIsAuthenticated(false);
            setUser(null);
            setToken(null);
            setError(error.message);
            addNotification({
                type: 'error',
                message: 'Login failed: ' + error.message
            });
        }
    }, [navigate, addNotification]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      localStorage.removeItem(config.tokenKey);
      localStorage.removeItem("user");
      sessionStorage.removeItem("google_credentials");
      navigate("/login");
      window.location.reload();
      addNotification({
        type: "success",
        message: "Successfully logged out",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, [navigate, addNotification]);

  const refreshToken = useCallback(async () => {
    try {
      const response = await api.post("/auth/refresh");
      if (response.data.token) {
        setToken(response.data.token);
        return response.data.token;
      }
      throw new Error("No token in refresh response");
    } catch (error) {
      console.error("Error refreshing token:", error);
      await logout();
      throw error;
    }
  }, [logout]);

  const value = {
    token,
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    refreshToken,
    calendlyLink,
    resumeState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
